/**
 * React Query hook for section history and restore operations
 * Story 232: View history of changes and undo them
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  HistoryListResponse,
  ReconstructedValueResponse,
  DeletedSectionItem,
  SectionRestoreResponse,
} from '../types/history';

/** Query key factory for history */
export const historyKeys = {
  all: ['history'] as const,
  entity: (entity: string, entityId: string) =>
    [...historyKeys.all, entity, entityId] as const,
  property: (entity: string, entityId: string, property: string) =>
    [...historyKeys.entity(entity, entityId), property] as const,
  deletedSections: (projectSlug: string, storyId: string, docType: string) =>
    ['deleted-sections', projectSlug, storyId, docType] as const,
};

/** Fetch history for an entity property */
async function fetchPropertyHistory(
  entity: string,
  entityId: string,
  property: string,
  limit?: number
): Promise<HistoryListResponse> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  const endpoint = `/api/v1/history/${entity}/${entityId}/${property}${params.toString() ? `?${params}` : ''}`;
  return api.fetch(endpoint);
}

/** Fetch all history for an entity */
async function fetchEntityHistory(
  entity: string,
  entityId: string,
  limit?: number
): Promise<HistoryListResponse> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  const endpoint = `/api/v1/history/${entity}/${entityId}${params.toString() ? `?${params}` : ''}`;
  return api.fetch(endpoint);
}

/** Fetch reconstructed value at a specific history entry */
async function fetchHistoryValue(
  historyId: string
): Promise<ReconstructedValueResponse> {
  return api.fetch(`/api/v1/history/entry/${historyId}/value`);
}

/** Restore deleted section */
async function restoreSection(
  sectionId: string
): Promise<SectionRestoreResponse> {
  return api.fetch(`/api/v1/sections/${sectionId}/restore`, {
    method: 'POST',
  });
}

/** Fetch deleted sections for a document */
async function fetchDeletedSections(
  projectSlug: string,
  storyId: string,
  docType: string
): Promise<{ deleted_sections: DeletedSectionItem[] }> {
  const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/documents/${docType}/deleted`;
  return api.fetch(endpoint);
}

/**
 * Hook to fetch history for a section's body property
 *
 * @param sectionId - Section internal ID (from database)
 * @param enabled - Whether to fetch (default true)
 */
export function useSectionBodyHistory(
  sectionId: string | null,
  enabled = true
) {
  return useQuery({
    queryKey: historyKeys.property('section', sectionId ?? '', 'body'),
    queryFn: () => fetchPropertyHistory('section', sectionId!, 'body'),
    enabled: enabled && !!sectionId,
  });
}

/**
 * Hook to fetch all history for a section
 *
 * @param sectionId - Section internal ID
 * @param enabled - Whether to fetch (default true)
 */
export function useSectionHistory(
  sectionId: string | null,
  enabled = true
) {
  return useQuery({
    queryKey: historyKeys.entity('section', sectionId ?? ''),
    queryFn: () => fetchEntityHistory('section', sectionId!),
    enabled: enabled && !!sectionId,
  });
}

/**
 * Hook to fetch reconstructed value at a history entry
 *
 * @param historyId - History entry ID
 * @param enabled - Whether to fetch (default true)
 */
export function useHistoryValue(historyId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['history-value', historyId],
    queryFn: () => fetchHistoryValue(historyId!),
    enabled: enabled && !!historyId,
  });
}

/**
 * Hook to fetch deleted sections for a document
 *
 * @param projectSlug - Project identifier
 * @param storyId - Story external ID
 * @param docType - Document type
 * @param enabled - Whether to fetch (default true)
 */
export function useDeletedSections(
  projectSlug: string,
  storyId: string | null,
  docType: string,
  enabled = true
) {
  return useQuery({
    queryKey: historyKeys.deletedSections(projectSlug, storyId ?? '', docType),
    queryFn: () => fetchDeletedSections(projectSlug, storyId!, docType),
    enabled: enabled && !!projectSlug && !!storyId,
  });
}

/**
 * Hook for section restore mutation
 *
 * @param projectSlug - Project identifier (for cache invalidation)
 * @param storyId - Story ID (for cache invalidation)
 * @param docType - Document type (for cache invalidation)
 */
export function useRestoreSection(
  projectSlug: string,
  storyId: string,
  docType: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sectionId: string) => restoreSection(sectionId),
    onSuccess: () => {
      // Invalidate document cache to show restored section
      queryClient.invalidateQueries({
        queryKey: ['story-documents', projectSlug, storyId, docType],
      });
      // Invalidate deleted sections list
      queryClient.invalidateQueries({
        queryKey: historyKeys.deletedSections(projectSlug, storyId, docType),
      });
    },
  });
}
